import {useEffect, useMemo, useRef, useState} from 'react';
import {useShopping} from '../contexts/ShoppingContext';
import {useLocation, useNavigate} from 'react-router-dom';
import {ActivePromosModal} from './ActivePromosModal';
import {useClientData} from '../hooks/useClientData';
import {usePreviewMode} from '../hooks/usePreviewMode';
import {DepartmentService} from '../services/departmentService';
import {ClientMessageBanner} from './header/ClientMessageBanner';
import {HeaderLogo} from './header/HeaderLogo';
import {HeaderActions} from './header/HeaderActions';
import {MobileMenu} from './header/MobileMenu';
import {DepartmentNav} from './header/DepartmentNav';

export function Header() {
    const {state, dispatch} = useShopping();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [showPromosModal, setShowPromosModal] = useState(false);
    const [isBannerExpanded, setIsBannerExpanded] = useState(true);
    const {clientData} = useClientData();
    const {isPreviewMode, toggle: togglePreviewMode} = usePreviewMode();
    const navigate = useNavigate();
    const location = useLocation();

    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Header height tracking
    const headerRef = useRef<HTMLElement>(null);

    const cartItemsCount = state.cart.reduce((total, item) => total + item.quantity, 0);

    // Get URL parameters
    const urlParams = new URLSearchParams(location.search);
    const selectedDepartmentId = urlParams.get('departmentId') ? Number(urlParams.get('departmentId')) : null;
    const selectedCategoryId = urlParams.get('categoryId') ? Number(urlParams.get('categoryId')) : null;
    const currentSearch = urlParams.get('search') || '';

    // Sync search query with URL on mount and location change
    useEffect(() => {
        setSearchQuery(currentSearch);
    }, [currentSearch]);

    // Debounce search query and update URL
    useEffect(() => {
        // Clear previous timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Don't trigger on initial mount if search is empty
        if (searchQuery === '' && currentSearch === '') {
            return;
        }

        // Don't trigger if searchQuery is already in sync with URL (e.g., on page refresh)
        if (searchQuery.trim() === currentSearch.trim()) {
            return;
        }

        // Set new timeout for debounce (500ms)
        searchTimeoutRef.current = setTimeout(async () => {
            const searchParams = new URLSearchParams(location.search);

            if (searchQuery.trim()) {
                // When searching, update departments with search filter
                try {
                    const departments = await DepartmentService.getDepartments(searchQuery.trim());
                    // Notify UI to update departments
                    if (typeof window !== 'undefined') {
                        const event = new CustomEvent('departmentsUpdated', {detail: departments});
                        window.dispatchEvent(event);
                    }
                } catch (error) {
                    console.warn('Failed to update departments for search:', error);
                }

                // Clear filters and set search parameter (reset page to 1)
                searchParams.delete('departmentId');
                searchParams.delete('categoryId');
                searchParams.delete('page');
                searchParams.set('search', searchQuery.trim());
            } else {
                // When clearing search, reload all departments
                try {
                    const departments = await DepartmentService.getDepartments();
                    // Notify UI to update departments
                    if (typeof window !== 'undefined') {
                        const event = new CustomEvent('departmentsUpdated', {detail: departments});
                        window.dispatchEvent(event);
                    }
                } catch (error) {
                    console.warn('Failed to reload departments after clearing search:', error);
                }

                // Remove search parameter (keep filters, reset page)
                searchParams.delete('search');
                searchParams.delete('page');
            }

            navigate(searchParams.toString() ? `/?${searchParams.toString()}` : '/');
        }, 500);

        // Cleanup timeout on unmount
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchQuery, currentSearch, navigate, location.search]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    // Get departments from state
    const departments = state.departments;

    // Get categories filtered by selected department (for mobile menu)
    const categories = useMemo(() => {
        if (!departments) return [];

        if (!selectedDepartmentId) {
            // No department selected - show all categories, deduplicated by ID
            const allCategories = departments.flatMap(dept => dept.productCategories || []);
            return allCategories.filter((category, index, self) =>
                index === self.findIndex(c => c.id === category.id)
            );
        }

        // Department selected - find matching department and return its categories
        const matchedDepartment = departments.find(dept =>
            Number(dept.id) === Number(selectedDepartmentId)
        );

        return matchedDepartment?.productCategories || [];
    }, [departments, selectedDepartmentId]);

    const handleDepartmentClick = (departmentId: number | null) => {
        const searchParams = new URLSearchParams();

        // Preserve search parameter if it exists
        if (currentSearch) {
            searchParams.set('search', currentSearch);
        }

        // Set department if selected
        if (departmentId !== null) {
            searchParams.set('departmentId', departmentId.toString());
        }

        // Clear category and page when changing departments
        navigate(searchParams.toString() ? `/?${searchParams.toString()}` : '/');
        setIsMenuOpen(false);
    };

    const handleCategoryClick = (categoryId: number) => {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('categoryId', categoryId.toString());
        searchParams.delete('page'); // Reset to page 1 when category changes
        navigate(`/?${searchParams.toString()}`);
        setIsMenuOpen(false);
    };

    const handleAllProductsClick = () => {
        const searchParams = new URLSearchParams();
        if (selectedDepartmentId) {
            searchParams.set('departmentId', selectedDepartmentId.toString());
        }
        // page is already not included
        navigate(searchParams.toString() ? `/?${searchParams.toString()}` : '/');
        setIsMenuOpen(false);
    };

    // Update header height CSS variable for CategoryNav positioning
    useEffect(() => {
        const updateHeaderHeight = () => {
            if (headerRef.current) {
                const height = headerRef.current.offsetHeight;
                document.documentElement.style.setProperty('--header-height', `${height}px`);
            }
        };

        updateHeaderHeight();
        window.addEventListener('resize', updateHeaderHeight);

        return () => {
            window.removeEventListener('resize', updateHeaderHeight);
        };
    }, [clientData?.clientMessage, isBannerExpanded]);

    const themeColors = clientData?.themeColors;

    const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleCartMouseEnter = () => {
        hoverTimeoutRef.current = setTimeout(() => {
            dispatch({type: 'SET_CART_OPEN', payload: true});
        }, 200);
    };

    const handleCartMouseLeave = () => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
    };

    if (!themeColors) return null;

    return (
        <header
            ref={headerRef}
            className="shadow-md sticky top-0 z-40 transition-colors duration-500"
            style={{backgroundColor: themeColors?.header_background}}
            role="banner"
        >
            {/* Client Message Banner */}
            {clientData?.clientMessage && (
                <ClientMessageBanner
                    message={clientData.clientMessage}
                    isExpanded={isBannerExpanded}
                    onToggle={() => setIsBannerExpanded(!isBannerExpanded)}
                />
            )}

            {/* Main Header */}
            <div className="px-4 sm:px-6 lg:px-8">
                <div className="flex items-center h-16">

                    <div className="flex-shrink-0 min-w-[160px]">
                        {clientData?.logoUrl && (
                            <HeaderLogo
                                logoUrl={clientData.logoUrl}
                                onLogoClick={() => handleDepartmentClick(null)}
                                textColor={themeColors?.header_text_color}
                            />
                        )}
                    </div>

                    {/* Search and Actions */}
                    <div className="ml-auto">
                        <HeaderActions
                            searchQuery={searchQuery}
                            onSearchChange={handleSearchChange}
                            isPreviewMode={isPreviewMode}
                            onTogglePreviewMode={togglePreviewMode}
                            cartItemsCount={cartItemsCount}
                            onCartMouseEnter={handleCartMouseEnter}
                            onCartMouseLeave={handleCartMouseLeave}
                            onShowPromos={() => setShowPromosModal(true)}
                            isMenuOpen={isMenuOpen}
                            onToggleMenu={() => setIsMenuOpen(!isMenuOpen)}
                            themeColors={themeColors}
                            showCart={location.pathname !== '/checkout'}
                        />
                    </div>
                </div>

                {/* Mobile Navigation */}
                {isMenuOpen && (
                    <MobileMenu
                        searchQuery={searchQuery}
                        onSearchChange={handleSearchChange}
                        departments={departments}
                        categories={categories}
                        selectedDepartmentId={selectedDepartmentId}
                        selectedCategoryId={selectedCategoryId}
                        onDepartmentClick={handleDepartmentClick}
                        onCategoryClick={handleCategoryClick}
                        onAllProductsClick={handleAllProductsClick}
                        themeColors={themeColors}
                    />
                )}
            </div>

            {/* Department Subheader - Desktop Only */}
            <DepartmentNav
                departments={departments}
                selectedDepartmentId={selectedDepartmentId}
                onDepartmentClick={handleDepartmentClick}
                themeColors={themeColors}
            />

            {/* Modals */}
            <ActivePromosModal
                isOpen={showPromosModal}
                onClose={() => setShowPromosModal(false)}
            />
        </header>
    );
}
